import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Menu, X, Youtube, Mail } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const NAV = [
  { to: "/puppies", label: "Available" },
  { to: "/litters", label: "Upcoming Litters" },
  { to: "/adopted", label: "Adopted" },
  { to: "/care", label: "Our Family" },
  { to: "/shipping", label: "Delivery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

const YOUTUBE = "https://youtube.com/@tinyteacuppuppies";
const TIKTOK = "https://www.tiktok.com/@teacuppuppy4sale?_r=1&_t=ZT-98ccnWnPPH3";
const EMAIL = "teacuppuppies4sale@gmail.com";

const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.5 3.4 3.5 3.6v2.4c-1.2.1-2.3-.3-3.5-1v6.1c0 3.6-2.4 5.9-5.6 5.9-3 0-5.4-2.2-5.4-5.2 0-3.1 2.4-5.3 5.6-5.1v2.6c-.4-.1-.9-.2-1.3-.1-1.3.2-2.1 1-2 2.4.1 1.3 1 2 2.2 2 1.3 0 2.2-1 2.2-2.6V3h2.7z"/>
  </svg>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#E2D1C3]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-heading text-xl md:text-2xl font-medium tracking-tight text-[#2A2118]">
            Teacup Puppies
          </span>
          <span className="label-eyebrow text-[#C8A98E] mt-0.5">4 Sale &middot; Est. Luxury</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-[#C8A98E] ${
                location.pathname === n.to ? "text-[#C8A98E]" : "text-[#2A2118]"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <a
            href={YOUTUBE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-youtube"
            className="text-[#2A2118] hover:text-[#C8A98E] transition-colors duration-300"
            aria-label="YouTube"
          >
            <Youtube size={20} />
          </a>
          <a
            href={TIKTOK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-tiktok"
            className="text-[#2A2118] hover:text-[#C8A98E] transition-colors duration-300"
            aria-label="TikTok"
          >
            <TikTokIcon size={19} />
          </a>
        </nav>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" aria-label="Menu" className="text-[#2A2118]">
                <Menu size={26} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#FDFBF7] border-[#E2D1C3] w-[80%]">
              <div className="flex items-center justify-between mb-10">
                <span className="font-heading text-xl text-[#2A2118]">Menu</span>
                <button onClick={() => setOpen(false)} aria-label="Close"><X size={22} /></button>
              </div>
              <div className="flex flex-col gap-5">
                {NAV.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-heading text-2xl text-[#2A2118] hover:text-[#C8A98E] transition-colors"
                  >
                    {n.label}
                  </Link>
                ))}
                <a href={YOUTUBE} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#2A2118] mt-4">
                  <Youtube size={20} /> YouTube
                </a>
                <a href={TIKTOK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#2A2118]">
                  <TikTokIcon size={19} /> TikTok
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#2A2118] text-[#F5E6E3] mt-32">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <h3 className="font-heading text-3xl font-light text-[#FDFBF7]">Teacup Puppies 4 Sale</h3>
          <p className="mt-4 text-sm text-[#d8c4b8] max-w-xs leading-relaxed">
            A boutique home raising the world&apos;s tiniest, healthiest teacup puppies — delivered with love across the USA and worldwide.
          </p>
        </div>
        <div>
          <p className="label-eyebrow text-[#C8A98E] mb-5">Explore</p>
          <div className="flex flex-col gap-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="text-sm text-[#d8c4b8] hover:text-[#FDFBF7] transition-colors w-fit">
                {n.label}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <p className="label-eyebrow text-[#C8A98E] mb-5">Connect</p>
          <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="flex items-center gap-2 text-sm text-[#d8c4b8] hover:text-[#FDFBF7] transition-colors">
            <Mail size={16} /> {EMAIL}
          </a>
          <a href={YOUTUBE} target="_blank" rel="noopener noreferrer" data-testid="footer-youtube" className="flex items-center gap-2 text-sm text-[#d8c4b8] hover:text-[#FDFBF7] transition-colors mt-3">
            <Youtube size={16} /> @tinyteacuppuppies
          </a>
          <a href={TIKTOK} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" className="flex items-center gap-2 text-sm text-[#d8c4b8] hover:text-[#FDFBF7] transition-colors mt-3">
            <TikTokIcon size={16} /> @teacuppuppy4sale
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between gap-3 text-xs text-[#a8988a]">
        <span>&copy; {new Date().getFullYear()} Teacup Puppies 4 Sale. All rights reserved.</span>
        <span>Raised as family. Delivered with love.</span>
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
};
